import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { UnifiedFilterProvider } from "./components/UnifiedGeoRoleFilter";
import "@/App.css";
import LandingPage from "./pages/LandingPage";
import QuotePage from './pages/QuotePage';
import LoginPage from "./pages/LoginPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ProfilePage from "./pages/ProfilePage";
import RegisterPage from "./pages/RegisterPage";
import RegisterPropertyPage from "./pages/RegisterPropertyPage";
import TenantDashboard from "./pages/TenantDashboard";
import ResidentAppPage from "./pages/resident/ResidentAppPage";
import PropertyManagerDashboard from "./pages/PropertyManagerDashboard";
import CompanyManagerDashboard from "./pages/CompanyManagerDashboard";
import StaffDashboard from "./pages/StaffDashboard";
import StaffAppMobile from "./pages/StaffAppMobile";
import AppErrorBoundary from "./components/AppErrorBoundary";
import ImpersonationBanner from "./components/ImpersonationBanner";
import AuthCallback from "./pages/AuthCallback";
import CertificateVerify from "./pages/CertificateVerify";
import PropertyTrustPage from "./pages/PropertyTrustPage";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import ContactPage from "./pages/ContactPage";
import CommercialServicesPage from "./pages/CommercialServicesPage";
import IndustryPage from "./pages/IndustryPage";
import ProposalPage from "./pages/ProposalPage";
import CleanersCommandCenterPage from "./pages/CleanersCommandCenterPage";
import CleanersDemoAccountPage from "./pages/CleanersDemoAccountPage";
import DemoSharePage from "./pages/DemoSharePage";
import LiveDemoPage from "./pages/LiveDemoPage";
import HelpDeskPage from "./pages/HelpDeskPage";
import DemoEndedPage from "./pages/DemoEndedPage";
import PlatformDemoPage from "./pages/PlatformDemoPage";
import { Toaster } from "./components/ui/sonner";
import SupportChatWidget from "./components/SupportChatWidget";
import SupportHandoffPage from './pages/SupportHandoffPage';
import { useAuth } from "./contexts/AuthContext";
import RewardsWorkspace from './pages/RewardsWorkspace';
import WorkforcePage from './pages/WorkforcePage';
import DeleteAccountPage from './pages/DeleteAccountPage';
import MobileAuthPage from './pages/MobileAuthPage';
import WarehousePage from './pages/WarehousePage';
import SharedPayRatesPage from './pages/SharedPayRatesPage';
import PurchasingPage from './pages/PurchasingPage';
import { NativeExperience } from './native/NativeExperience';

function AppRouter() {
  const location = useLocation();
  
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/quote" element={<QuotePage />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/delete-account" element={<DeleteAccountPage />} />
      <Route path="/mobile-auth/:requestId" element={<MobileAuthPage />} />
      <Route path="/attendance" element={<WorkforcePage />} />
      <Route path="/workforce-payroll" element={<WorkforcePage payroll />} />
      <Route path="/time-corrections" element={<WorkforcePage corrections />} />
      <Route path="/warehouse" element={<WarehousePage />} />
      <Route path="/pay-rates" element={<SharedPayRatesPage />} />
      <Route path="/suppliers" element={<PurchasingPage />} />
      <Route path="/finance-payables" element={<PurchasingPage finance />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/commercial-services" element={<CommercialServicesPage />} />
      <Route path="/commercial-services/:type" element={<IndustryPage />} />
      <Route path="/demo" element={<PlatformDemoPage />} />
      <Route path="/admin/proposal/:id" element={<ProposalPage />} />
      <Route path="/cleaners-command-center" element={<CleanersCommandCenterPage />} />
      <Route path="/cleaners-demo/:type" element={<CleanersDemoAccountPage />} />
      <Route path="/demo-share/:token" element={<DemoSharePage />} />
      <Route path="/live-demo" element={<LiveDemoPage />} />
      <Route path="/help-desk" element={<HelpDeskPage />} />
      <Route path="/demo-ended" element={<DemoEndedPage />} />
      <Route path="/verify/:certId" element={<CertificateVerify />} />
      <Route path="/property/:propertyId/trust" element={<PropertyTrustPage />} />
      <Route path="/register-property" element={<RegisterPropertyPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/support-handoff/:token" element={<SupportHandoffPage />} />
      <Route path="/staff-login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/profile" element={<ProfilePage />} />
      <Route path="/resident-rewards" element={<RewardsWorkspace view="resident" />} />
      <Route path="/staff-rewards" element={<RewardsWorkspace view="staff" />} />
      <Route path="/rewards-review" element={<RewardsWorkspace view="review" />} />
      <Route path="/resident-rewards-admin" element={<RewardsWorkspace view="resident-admin" />} />
      <Route path="/operation-records" element={<RewardsWorkspace view="operations" />} />
      <Route path="/shift-briefings" element={<RewardsWorkspace view="briefings" />} />
      <Route path="/necs-standard-review" element={<RewardsWorkspace view="standards-review" />} />
      <Route path="/closeout-attention" element={<RewardsWorkspace view="attention" />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/tenant-dashboard" element={<TenantDashboard />} />
      <Route path="/resident" element={<ResidentAppPage />} />
      {/* Legacy /driver-dashboard removed — all field staff use /staff-dashboard */}
      <Route path="/driver-dashboard" element={<Navigate to="/staff-dashboard" replace />} />
      <Route path="/property-manager-dashboard" element={<PropertyManagerDashboard />} />
      {/* Iter-78 (Master Architecture P0) — CEO / admin / company_manager
           get their full 12-department Executive HQ dashboard back. Manager
           / Supervisor / Crew Leader / Agent stay on the unified
           StaffDashboard. `/company-dashboard` legacy alias still resolves. */}
      <Route path="/company-manager-dashboard" element={<CompanyManagerDashboard />} />
      <Route path="/company-dashboard" element={<Navigate to="/company-manager-dashboard" replace />} />
      <Route path="/staff-dashboard" element={<StaffDashboard />} />
      <Route path="/staff-app" element={<StaffAppMobile />} />
    </Routes>
  );
}

function GlobalSupportChat() {
  const { user } = useAuth();
  const location = useLocation();
  const role = user?.role || 'public';
  if (['/login', '/staff-login', '/forgot-password', '/reset-password'].includes(location.pathname)) return null;
  return <SupportChatWidget userRole={role} />;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppErrorBoundary>
          <AuthProvider>
            <UnifiedFilterProvider>
              {/* Iter-158 · Phase 1 — Impersonation banner removed from
                  all PM dashboards (Valet + Cleaners) per spec. It renders
                  ONLY inside a per-tab demo window (side-by-side comparison)
                  as the affordance to identify and close that window. */}
              <ImpersonationBanner />
              <NativeExperience><AppRouter /><GlobalSupportChat /></NativeExperience>
              <Toaster richColors position="top-right" />
            </UnifiedFilterProvider>
          </AuthProvider>
        </AppErrorBoundary>
      </BrowserRouter>
    </div>
  );
}

export default App;